import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const PANCHINA_REASONS = ["Non fa più fotovoltaico", "Screzio con noi", "Difficoltà di periodo"];
const CIMITERO_REASONS = ["Cessata attività / chiusura"];

// kind: "prospects" | "partners"; target: {id, name}; mode: "move" | "restore"
export default function PresidioDialog({ open, onOpenChange, kind, target, mode = "move", onDone }) {
  const [dest, setDest] = useState("panchina");
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setDest("panchina"); setReason(""); setNote(""); } }, [open]);

  const reasons = dest === "cimitero" ? CIMITERO_REASONS : PANCHINA_REASONS;
  const label = kind === "partners" ? "cliente" : "prospect";

  const submit = async () => {
    const status = mode === "restore" ? "attivo" : dest;
    if (status !== "attivo" && !reason) { toast.error("Seleziona un motivo"); return; }
    if (status !== "attivo" && !note.trim()) { toast.error("La nota è obbligatoria"); return; }
    setBusy(true);
    try {
      await api.post(`/${kind}/${target.id}/presidio`, { status, reason: status === "attivo" ? "" : reason, note });
      toast.success(status === "attivo" ? "Riportato tra gli attivi" : status === "panchina" ? "Spostato in Panchina" : "Spostato in Cimitero");
      onOpenChange(false);
      onDone?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="presidio-dialog">
        <DialogHeader>
          <DialogTitle>{mode === "restore" ? "Riporta tra gli attivi" : "Sospendi dal monitoraggio"}</DialogTitle>
          <DialogDescription>{target?.name}</DialogDescription>
        </DialogHeader>

        {mode === "restore" ? (
          <p className="text-sm text-muted-foreground">
            Il {label} tornerà nel monitoraggio attivo e nei conteggi. Confermi?
          </p>
        ) : (
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Destinazione</Label>
              <Select value={dest} onValueChange={(v) => { setDest(v); setReason(""); }}>
                <SelectTrigger data-testid="presidio-dest"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="panchina">Panchina — esce dal monitoraggio (resta nei conteggi)</SelectItem>
                  <SelectItem value="cimitero">Cimitero — esce dal monitoraggio e dai conteggi</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground">
                {dest === "cimitero"
                  ? "Da usare solo per aziende cessate / chiuse. Torna attivo in automatico dopo un nuovo ordine."
                  : "Non si occupa più di fotovoltaico, screzio con noi, o difficoltà di periodo. Torna attivo in automatico dopo un nuovo ordine."}
              </p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Motivo *</Label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger data-testid="presidio-reason"><SelectValue placeholder="Seleziona un motivo" /></SelectTrigger>
                <SelectContent>{reasons.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Nota *</Label>
              <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="presidio-note" placeholder="Nota obbligatoria: dettagli sullo spostamento…" />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={submit} disabled={busy} data-testid="presidio-save">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : mode === "restore" ? "Riporta attivo" : "Conferma"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
